import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Description as FaxIcon,
  NotificationsActive as BellIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import TriageBoard from '../components/Dashboard/TriageBoard';
import { faxSignaturesApi, faxFilingsApi } from '../services/api';
import SignFaxViewer from '../components/common/SignFaxViewer';
import FaxesFiledDrawer from '../components/Dashboard/FaxesFiledDrawer';
import NewTaskModal from '../components/Dashboard/NewTaskModal';

const MODALITY_OPTIONS = [
  { value: 'all', label: 'All Modalities' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'laboratory', label: 'Laboratory' },
  { value: 'prescriptions', label: 'Prescriptions (Rx)' },
  { value: 'telehealth', label: 'Tele-Health' },
  { value: 'diagnostics', label: 'Diagnostics (Radiology)' },
  { value: 'general', label: 'General' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'todo', label: 'To Do' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'needs_reply', label: 'Needs reply' },
];

const Dashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Category View is the default (primary) view
  const [viewMode, setViewMode] = useState<'status' | 'category'>('category');
  const [modality, setModality] = useState('all');
  const [status, setStatus] = useState('all');
  // Tasks the office owes a reply on, counted by the board from what it loaded.
  const [needsReply, setNeedsReply] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [signatureDrawerOpen, setSignatureDrawerOpen] = useState(false);
  const [signatureQueue, setSignatureQueue] = useState<any[]>([]);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signDialogTask, setSignDialogTask] = useState<any>(null);
  const [filedDrawerOpen, setFiledDrawerOpen] = useState(false);
  const [filedHoldCount, setFiledHoldCount] = useState(0);
  const [filedJustCount, setFiledJustCount] = useState(0);

  const handleRefresh = () => {
    // Trigger refresh of triage board
    window.location.reload();
  };

  const handleNewTaskCreated = () => {
    // Refresh the page to show the new task
    window.location.reload();
  };



  useEffect(() => {
    faxSignaturesApi.queue().then((res) => {
      setSignatureQueue(res.data?.data || []);
    }).catch(() => {});
    faxFilingsApi.queue().then((res) => {
      setFiledHoldCount(res.data?.holdCount || res.data?.data?.hold?.length || 0);
      setFiledJustCount(res.data?.filedCount || res.data?.data?.filed?.length || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get('faxFiled') !== '1') return;
    setFiledDrawerOpen(true);
    q.delete('faxFiled');
    const search = q.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  const loadSignatureQueue = async () => {
    setSignatureLoading(true);
    try {
      const res = await faxSignaturesApi.queue();
      setSignatureQueue(res.data?.data || []);
    } catch (e) {
      console.error('Failed to load signature fax queue', e);
      setSignatureQueue([]);
    } finally {
      setSignatureLoading(false);
    }
  };

  const openSignatures = async () => {
    setSignatureDrawerOpen(true);
    await loadSignatureQueue();
  };

  return (
    <Box>
      {/* Header — density pass: h5 title, mb 3→2 */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Triage Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage incoming patient messages and tasks
          </Typography>
        </Box>

        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* View Mode Tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,  borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={viewMode}
          onChange={(_, value) => {
            setViewMode(value);
            // Completed lives only in the single Completed column. Leaving that
            // view for Category would empty the four category columns.
            if (value === 'category' && status === 'completed') {
              setStatus('all');
            }
          }}
          sx={{ minHeight: 40 }}
        >
          <Tab 
            label="Category View" 
            value="category"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
          <Tab 
            label="Status View" 
            value="status"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>
        <Badge
          badgeContent={signatureQueue.length || 0}
          color="error"
          invisible={!signatureQueue.length}
          sx={{ alignSelf: 'center', ml: { xs: 0.5, sm: 1 }, flexShrink: 0 }}
        >
          <Button
            size="small"
            variant="outlined"
            color={signatureQueue.length ? 'error' : 'primary'}
            startIcon={signatureQueue.length ? <BellIcon /> : <FaxIcon />}
            onClick={openSignatures}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minHeight: 40,
              ...(signatureQueue.length
                ? { borderColor: 'error.main', color: 'error.main' }
                : {}),
            }}
          >
            Faxes to sign
          </Button>
        </Badge>
        <Badge
          badgeContent={filedHoldCount || filedJustCount || 0}
          color="error"
          invisible={!filedHoldCount && !filedJustCount}
          sx={{ alignSelf: 'center', ml: { xs: 0.5, sm: 1 }, flexShrink: 0, '& .MuiBadge-badge': { fontWeight: 700 } }}
        >
          <Button
            size="small"
            variant="outlined"
            color={(filedHoldCount || filedJustCount) ? 'error' : 'primary'}
            startIcon={(filedHoldCount || filedJustCount) ? <BellIcon /> : <FaxIcon />}
            onClick={() => setFiledDrawerOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minHeight: 40,
              ...((filedHoldCount || filedJustCount)
                ? { borderColor: 'error.main', color: 'error.main' }
                : {}),
            }}
          >
            Fax Filing
          </Button>
        </Badge>
      </Box>

      {/* Filters */}
      <Box 
        sx={{
          display: 'flex',
          gap: 1.25,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
          p: 1.25,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {/* Search */}
        <TextField
          placeholder="Search patients..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 250 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" edge="end" onClick={() => setSearchQuery('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        {/* Modality Filter */}
        <FormControl size="small" sx={{ flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' }, minWidth: { sm: 180 } }}>
          <InputLabel>Modality</InputLabel>
          <Select
            value={modality}
            label="Modality"
            onChange={(e) => setModality(e.target.value)}
          >
            {MODALITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status filter — segmented control instead of a dropdown (density
            pass: 4 options are always visible, one tap instead of two). */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(_, v) => {
            if (v === null) return;
            setStatus(v);
            if (v === 'completed') setViewMode('status');
            else setViewMode('category');
          }}
          aria-label="Filter by status"
          sx={{
            flex: { xs: '1 1 100%', sm: '0 0 auto' },
            // Five options no longer fit a phone in one row; let them wrap
            // rather than clip the one that was added for the reply queue.
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 1.25,
              py: 0.5,
              fontSize: '0.78rem',
              whiteSpace: 'nowrap',
              flex: { xs: '1 1 auto', sm: '0 0 auto' },
            },
          }}
        >
          {STATUS_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.value === 'all' ? 'All' : option.label}
              {option.value === 'needs_reply' && needsReply !== null ? ` (${needsReply})` : ''}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* New Task Button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsNewTaskModalOpen(true)}
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          + New Task
        </Button>

        {/* Active Filters Display */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {modality !== 'all' && (
            <Chip
              label={MODALITY_OPTIONS.find(o => o.value === modality)?.label}
              onDelete={() => setModality('all')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {status !== 'all' && (
            <Chip
              label={STATUS_OPTIONS.find(o => o.value === status)?.label}
              onDelete={() => {
                setStatus('all');
                setViewMode('category');
              }}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Triage Board */}
      <TriageBoard 
        viewMode={viewMode}
        modalityFilter={modality}
        statusFilter={status}
        searchQuery={searchQuery}
        onNeedsReply={setNeedsReply}
      />


      <Drawer anchor="right" open={signatureDrawerOpen} onClose={() => setSignatureDrawerOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Faxes to sign</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Sign-and-return faxes found by reading the full PDF. Open a row to see every page, confirm the return number, and click where the signature goes. Anything else is filed and does not become a dashboard task.
          </Typography>
          <Button size="small" onClick={loadSignatureQueue} sx={{ mb: 1 }}>Refresh</Button>
          {signatureLoading ? (
            <Typography variant="body2">Loading…</Typography>
          ) : signatureQueue.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Queue empty.</Typography>
          ) : (
            <List dense>
              {signatureQueue.map((t) => (
                <Box key={String(t.id)} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.5 }}>
                  <ListItemButton onClick={() => setSignDialogTask(t)}>
                    <ListItemText
                      primary={t.patientName || 'Fax'}
                      secondary={`${t.patientPhone || ''} · ${t.faxClassifyReason || 'signature'} · ${(t.messageText || '').slice(0, 80)}`}
                    />
                  </ListItemButton>
                  <Box sx={{ display: 'flex', gap: 1, px: 2, pb: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setSignDialogTask(t)}
                    >
                      Sign & return fax
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={async () => {
                        try {
                          await faxSignaturesApi.setLane(t.id, 'filing');
                          await loadSignatureQueue();
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                    >
                      Send to filing
                    </Button>
                  </Box>
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      <FaxesFiledDrawer
        open={filedDrawerOpen}
        onClose={() => {
          setFiledDrawerOpen(false);
          faxFilingsApi.queue().then((res) => {
            setFiledHoldCount(res.data?.holdCount || res.data?.data?.hold?.length || 0);
            setFiledJustCount(res.data?.filedCount || res.data?.data?.filed?.length || 0);
          }).catch(() => {});
        }}
      />

      {signDialogTask && (
        <SignFaxViewer
          task={signDialogTask}
          onClose={() => setSignDialogTask(null)}
          onDone={async () => {
            setSignDialogTask(null);
            await loadSignatureQueue();
          }}
        />
      )}

      {/* New Task Modal */}
      <NewTaskModal
        open={isNewTaskModalOpen}
        onClose={() => setIsNewTaskModalOpen(false)}
        onTaskCreated={handleNewTaskCreated}
      />
    </Box>
  );
};

export default Dashboard;

